import Vue from 'vue'
import firebase from 'firebase/app'
import 'firebase/database'
import Cookies from 'js-cookie'
import { functions } from '@/firebase'
import { duplicatedExperienceAction } from '@/utils/logsHelpers'
import GiphyAPI from 'giphy-api'

export default {
  namespaced: true,
  state: {
    loadedExperience: null,
    loadedProject: null,
    experienceRef: null,
    activeTextEditor: null,
    activeWidgetBounds: null,
    activeWidget: null,
    choosingAsset: false,
    choosingAssetType: null,
    chosenAsset: null,
    widgetEditingConfig: {},
    signupInProgress: false,
    signedUp: false,
    widgetDragDisabled: false,
    handoverId: null,
    templates: {},
    templatesRef: null,
    colorPickerPreset: null,
    experienceCookies: null, // True: accepts cookies, False: rejects cookies: Null: no preference set yet
    preloadedAssets: {},
    builderSnapshots: [],
    resultFrameCalculator: {},
    resultFrameVotes: [],
    chosenOptionIcon: null,
    pageIndex: null,
    smartResultIndex: null,
    selectedWidgetId: null,
    projectPages: [{
      exists: true,
      dimensions: {
        w: 340,
        h: 678
      }
    }],
    previewMode: null,
    publishMode: null,
    templateGalleryStatus: null,
    ratio: null,
    redosBuffer: [],
    preloadedGifs: null,
    activeMicrositeName: null
  },
  getters: {
    loadedExperience (state) {
      return state.loadedExperience
    },
    getColorPickerPreset (state) {
      if (state.colorPickerPreset) {
        return state.colorPickerPreset
      } else if (Cookies.get('colorPickerPreset')) {
        return JSON.parse(Cookies.get('colorPickerPreset'))
      }
      return []
    },
    askForCookies (state) {
      return state.experienceCookies == null
    },
    acceptsCookies (state) {
      return state.experienceCookies != null && state.experienceCookies
    },
    preloadedAssets (state, getters, rootState, rootGetters) {
      return {
        ...(rootState.assets ? rootGetters['assets/savedAssets'] : {}),
        ...state.preloadedAssets
      }
    },
    getResultFrameVotes (state) {
      return state.resultFrameVotes
    },
    getChosenOptionIcon (state) {
      return state.chosenOptionIcon
    },
    getPageIndex (state) {
      return state.pageIndex
    },
    getSmartResultIndex (state) {
      return state.smartResultIndex
    },
    getSelectedWidgetId (state) {
      return state.selectedWidgetId
    },
    getProjectPages (state) {
      return state.projectPages
    },
    getPreviewMode (state) {
      return state.previewMode
    },
    getPublishMode (state) {
      return state.publishMode
    },
    getTemplateGalleryStatus (state) {
      return state.templateGalleryStatus
    },
    getActiveMicrositeName (state) {
      return state.activeMicrositeName
    }
  },
  mutations: {
    setPageIndex (state, pageIndex) {
      state.pageIndex = pageIndex
    },
    setSmartResultIndex (state, pageIndex) {
      state.smartResultIndex = pageIndex
    },
    setSelectedWidgetId (state, widgetId) {
      state.selectedWidgetId = widgetId
    },
    setProjectPages (state, pages) {
      if (pages.length) {
        state.projectPages = pages
      } else {
        state.projectPages = [{
          exists: true,
          dimensions: {
            w: 340,
            h: 678
          }
        }]
      }
    },
    setPreviewMode (state, status) {
      state.previewMode = status
    },
    setPublishMode (state, status) {
      state.publishMode = status
    },
    setTemplateGalleryStatus (state, status) {
      state.templateGalleryStatus = status
    },

    setLoadedExperience (state, payload) {
      state.loadedExperience = payload || false
    },
    setExperienceRef (state, payload) {
      state.experienceRef = payload
    },
    setActiveTextEditor (state, editor) {
      state.activeTextEditor = editor
    },
    setActiveWidgetBounds (state, bounds) {
      state.activeWidgetBounds = bounds
    },
    setChoosingAsset (state, { type, widgetId, ratio }) {
      state.choosingAsset = widgetId
      state.choosingAssetType = type
      state.ratio = ratio
    },
    setChosenAsset (state, asset) {
      state.chosenAsset = asset
    },
    resetChoosingAsset (state) {
      state.chosenAsset = null
      state.choosingAsset = false
      state.choosingAssetType = null
    },
    setSignupInProgress (state, signingUp) {
      state.signupInProgress = signingUp
    },
    setSignedUp (state, signedUp) {
      state.signedUp = signedUp
    },
    setWidgetDragDisabled (state, disabled) {
      state.widgetDragDisabled = disabled
    },
    setHandoverId (state, id) {
      state.handoverId = id
    },
    setTemplates (state, templates) {
      state.templates = templates
    },
    setTemplatesRef (state, ref) {
      state.templatesRef = ref
    },
    setWidgetEditingConfig (state, config) {
      state.widgetEditingConfig = config
    },
    setColorPickerPreset (state, newPreset) {
      Cookies.set('colorPickerPreset', newPreset)
      state.colorPickerPreset = newPreset
    },
    setExperienceCookiePrefs (state, prefs) {
      state.experienceCookies = prefs
    },
    setPreloadedAsset (state, payload) {
      Vue.set(state.preloadedAssets, payload.id, payload.asset)
    },
    setBuilderSnapshot (state, payload) {
      state.builderSnapshots.push(payload)
    },
    setRedosBuffer (state, payload) {
      state.redosBuffer = payload
    },
    addResultFrameOption (state, payload) {
      if (!state.resultFrameCalculator[payload.widgetId]) {
        state.resultFrameCalculator[payload.widgetId] = []
      }
      state.resultFrameCalculator[payload.widgetId] = payload.selectedSymbol

      const results = state.resultFrameCalculator
      let votes = []

      for (const widget in results) {
        if (results[widget] && results[widget][0]) {
          votes = [...votes, ...results[widget]]
        }
      }

      state.resultFrameVotes = votes
    },
    setOptionIcon (state, icon) {
      state.chosenOptionIcon = icon
    },
    cleanSnapshotsBuffer (state) {
      state.builderSnapshots = []
    },
    setLoadedProject (state, payload) {
      state.loadedProject = payload
    },
    setPreloadedGids (state, payload) {
      state.preloadedGifs = payload
    },
    setActiveMicrositeName (state, name) {
      state.activeMicrositeName = name
    }
  },
  actions: {
    async preloadGifs ({ commit }) {
      const giphy = GiphyAPI({
        apiKey: 'GXuT2ehxuZFqJvTGEpVrlSSOFJFjZKQm',
        https: true
      })
      const response = await giphy.trending({
        api: 'stickers',
        rating: 'pg',
        limit: 20
      })
      commit('setPreloadedGids', response)
    },
    async saveExperience ({}, { pageData }) {
      const pageRef = await firebase
        .database()
        .ref('sketch')
        .push(pageData)
      return pageRef.key
    },
    async updateExperience ({}, { id, pageData }) {
      await firebase
        .database()
        .ref('sketch')
        .child(id)
        .set(pageData)
    },
    loadExperience ({ state, commit }, { id }) {
      if (state.experienceRef) {
        state.experienceRef.off()
      }

      if (state.loadedExperience) {
        commit('setLoadedExperience', null)
      }

      const experienceRef = firebase.database().ref('sketch').child(id)
      experienceRef.on('value', snapshot => {
        commit('setLoadedExperience', snapshot.val())
      })
      commit('setExperienceRef', experienceRef)
    },
    unloadExperience ({ state, commit }) {
      if (state.experienceRef) {
        state.experienceRef.off()
      }
      commit('setLoadedExperience', null)
      commit('setExperienceRef', null)
    },
    getExperiences ({}, ids) {
      const promises = []

      ids.map(x => {
        promises.push(
          firebase
            .database()
            .ref('sketch')
            .child(x)
            .once('value', (snapshot) => {
              return snapshot.val()
            })
        )
      })

      return Promise.all(promises)
    },
    async getExperience ({ commit }, { id }) {
      if (!id) return
      const snapshot = await firebase
        .database()
        .ref('sketch')
        .child(id)
        .once('value')
      const experience = snapshot.val()

      const experienceToUpdate = {}
      experienceToUpdate[id] = experience
      commit('test/setAllLoadedExperiences', experienceToUpdate, { root: true })

      return experience
    },
    async duplicateExperience ({ dispatch, rootState }, experienceId) {
      const pageData = await dispatch('getExperience', { id: experienceId })
      const newExperienceId = await dispatch('saveExperience', { pageData })

      duplicatedExperienceAction(rootState.auth.user,
        {
          experience_action: true,
          parent_experience_id: experienceId,
          new_experience_id: newExperienceId,
          message: `Duplicated ${experienceId} to ${newExperienceId} (reediting)`
        })

      return newExperienceId
    },
    async saveSession ({}, { id, session }) {
      if (!id) {
        session.date = Date.now()
        const sessionRef = await firebase
          .database()
          .ref('experience_session')
          .push(session)
        return sessionRef.key
      } else {
        await firebase
          .database()
          .ref('experience_session')
          .child(id)
          .set(session)
        return id
      }
    },
    // async resyncSessions (context, testId) {
    //   await functions.httpsCallable('resyncExperienceTotals')({ testId, simulate: false })
    // },
    async loadSessions ({}, id) {
      if (id) {
        const snapshot = await firebase
          .database()
          .ref('experience_session')
          .orderByChild('test')
          .equalTo(id)
          .once('value')
        return snapshot.val()
      } else return []
    },
    async executeHandover ({ state, dispatch }, { username, uid, billing, enterprise }) {
      if (state.handoverId && uid && username) {
        let tapkitBrandingNeeded = true
        if ((billing && billing.sub) || enterprise) tapkitBrandingNeeded = false

        await dispatch('test/updateTest', {
          testId: state.handoverId,
          test: {},
          update: {
            enterprise: null,
            user_enterprise: (enterprise && enterprise.id) || null,
            user_uid: uid,
            user_username: username,
            experience_config: {
              branding: tapkitBrandingNeeded
            }
          }
        }, { root: true })
      }
    },
    async saveExperienceTemplate ({ dispatch }, { id, template }) {
      if (id) {
        await firebase
          .database()
          .ref('re_template')
          .child(id)
          .set(template)
        return id
      } else {
        template.experience = await dispatch('duplicateExperience', template.experience)
        const ref = await firebase
          .database()
          .ref('re_template')
          .push(template)
        return ref.key
      }
    },
    async removeTemplate (context, { id, template }) {
      if (id) {
        await firebase
          .database()
          .ref('re_template')
          .child(id)
          .remove()
        if (template && template.experience) {
          await firebase
            .database()
            .ref('sketch')
            .child(template.experience)
            .remove()
        }
      }
    },
    async loadTemplates ({ state, commit }) {
      if (state.templatesRef) {
        // Make sure we only register one value listener at a time
        state.templatesRef.off()
      }

      const templatesRef = firebase
        .database()
        .ref('re_template')

      commit('setTemplatesRef', templatesRef)

      templatesRef.on('value', snapshot => {
        commit('setTemplates', snapshot.val())
      })
    },
    async captureUnfurl (context, { test, domain, slug, shop }) {
      const result = await functions.httpsCallable('renderUnfurl')({ test, domain, slug, shop })
      if (result) return result.data
      else return null
    },
    checkExperienceCookies ({ commit }, { slug }) {
      if (slug) {
        if (localStorage.getItem(`accept-cookies-${slug}`)) {
          commit('setExperienceCookiePrefs', true)
          return true
        } else if (localStorage.getItem(`reject-cookies-${slug}`)) {
          commit('setExperienceCookiePrefs', false)
          return false
        } else commit('setExperienceCookiePrefs', null)
      }
      return null
    },
    setExperienceCookiePrefs ({ commit }, { slug, accept }) {
      if (slug && accept != null) {
        if (accept) localStorage.setItem(`accept-cookies-${slug}`, 1)
        else localStorage.setItem(`reject-cookies-${slug}`, 1)
        commit('setExperienceCookiePrefs', accept)
      }
    },
    async getAsset ({ getters, commit }, id) {
      if (getters.preloadedAssets && getters.preloadedAssets[id]) {
        return getters.preloadedAssets[id]
      } else {
        const snapshot = await firebase.database().ref('favorites').child(id).once('value')
        const asset = snapshot.val()
        commit('setPreloadedAsset', { id, asset })
        return asset
      }
    },
    addBuilderSnapshot ({ commit }, payload) {
      commit('setBuilderSnapshot', payload)
    },
    cleanBuilderSnapshotsBuffer ({ commit }) {
      commit('cleanSnapshotsBuffer')
    },
    setResultFrameOption ({ commit }, { widgetId, selectedSymbol }) {
      commit('addResultFrameOption', { widgetId, selectedSymbol })
    },
    async searchForIcons (context, searchString) {
      const icons = await functions.httpsCallable('assets-getNounProjectIcons')({ keyword: searchString })
      return icons
    },
    setOptionIcon ({ commit }, icon) {
      commit('setOptionIcon', icon)
    }
  }
}
